import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import {
  Plus,
  Search,
  Download,
  FileText,
  Eye,
  Edit,
  Package,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  creditDebitNoteApiService,
  CreditDebitNoteHeader,
  CreditDebitNoteListStatus,
} from '../services/creditDebitNoteApi';
import { formatDate } from '../utils/dateUtils';
import { useBranch } from '../contexts/BranchContext';

function normalizeListStatus(row: CreditDebitNoteHeader): string {
  const raw = (row as { status?: string }).status;
  return String(raw ?? '')
    .trim()
    .toUpperCase();
}

function statusBadgeClass(statusNorm: string): string {
  if (statusNorm === CreditDebitNoteListStatus.E_INVOICED) {
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  }
  if (statusNorm === CreditDebitNoteListStatus.DRAFT) {
    return 'bg-amber-50 text-amber-900 border-amber-200';
  }
  return 'bg-slate-50 text-slate-800 border-slate-200';
}

function statusDisplayLabel(statusNorm: string): string {
  if (!statusNorm) return '—';
  if (statusNorm === CreditDebitNoteListStatus.E_INVOICED) return 'E-Invoiced';
  if (statusNorm === CreditDebitNoteListStatus.DRAFT) return 'Draft';
  return statusNorm.replace(/_/g, ' ');
}

export function CreditDebitNoteListingPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [list, setList] = useState<CreditDebitNoteHeader[]>([]);
  const [filtered, setFiltered] = useState<CreditDebitNoteHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState('');
  const [invoiceDateTo, setInvoiceDateTo] = useState('');

  const loadList = useCallback(async () => {
    if (selectedBranch == null) {
      setList([]);
      setFiltered([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await creditDebitNoteApiService.getList(selectedBranch);
      setList(data);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const applyFilters = useCallback(() => {
    let rows = [...list];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const noteNo = (n: CreditDebitNoteHeader) =>
        (n as { noteNumber?: string }).noteNumber ?? n.creditDebitNoteNo ?? '';
      const invNo = (n: CreditDebitNoteHeader) =>
        (n as { referenceInvoiceNo?: string }).referenceInvoiceNo ?? n.invoiceNo ?? '';
      rows = rows.filter(
        (n) =>
          noteNo(n).toLowerCase().includes(q) ||
          invNo(n).toLowerCase().includes(q) ||
          (n.noteType && String(n.noteType).toLowerCase().includes(q)) ||
          normalizeListStatus(n).toLowerCase().includes(q)
      );
    }
    if (statusFilter && statusFilter !== 'all') {
      rows = rows.filter((n) => normalizeListStatus(n) === statusFilter);
    }
    if (invoiceDateFrom) {
      rows = rows.filter((n) => {
        const invoiceDate = n.invoiceDate?.split(/[T ]/)[0];
        return !!invoiceDate && invoiceDate >= invoiceDateFrom;
      });
    }
    if (invoiceDateTo) {
      rows = rows.filter((n) => {
        const invoiceDate = n.invoiceDate?.split(/[T ]/)[0];
        return !!invoiceDate && invoiceDate <= invoiceDateTo;
      });
    }
    setFiltered(rows);
  }, [list, searchQuery, statusFilter, invoiceDateFrom, invoiceDateTo]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleView = (id: number) => navigate(`/invoices/credit-debit-notes/${id}/view`);
  const handleEdit = (id: number) => navigate(`/invoices/credit-debit-notes/${id}/edit`);
  const handleNew = () => navigate('/invoices/credit-debit-notes/new');

  const exportToCSV = () => {
    const headers = [
      'Credit/Debit No',
      'Type',
      'Status',
      'Invoice No',
      'Invoice Date',
      'Amount',
    ];
    const csvData = filtered.map((row) => {
      const noteNumber = (row as { noteNumber?: string }).noteNumber ?? row.creditDebitNoteNo ?? `#${row.id}`;
      const referenceInvoiceNo =
        (row as { referenceInvoiceNo?: string }).referenceInvoiceNo ?? row.invoiceNo ?? '—';
      const invoiceDate = row.invoiceDate ?? (row as { noteDate?: string }).noteDate ?? row.creditDate;
      const typeLabel =
        row.noteType === 'DEBIT_NOTE' || String(row.noteType).toUpperCase() === 'DEBIT' ? 'Debit Note' : 'Credit Note';
      return [
        noteNumber,
        typeLabel,
        normalizeListStatus(row) || '',
        referenceInvoiceNo,
        invoiceDate ? formatDate(invoiceDate) : '',
        row.grandTotal != null ? String(row.grandTotal) : '',
      ];
    });
    const csvContent = [headers, ...csvData]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit_debit_notes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Credit / Debit Notes</CardTitle>
                  <CardDescription className="text-primary-100">
                    Manage credit and debit notes against invoices
                  </CardDescription>
                </div>
              </div>
              <Button
                onClick={handleNew}
                className="bg-white text-primary-700 hover:bg-primary-50 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Note
              </Button>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="cdn-search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="cdn-search"
                    placeholder="Search by note no, invoice no, type, status..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 border-primary-200 focus-visible:ring-primary-400"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdn-status">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="cdn-status" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value={CreditDebitNoteListStatus.DRAFT}>DRAFT</SelectItem>
                    <SelectItem value={CreditDebitNoteListStatus.E_INVOICED}>E_INVOICED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdn-inv-from">Invoice Date From</Label>
                <Input
                  id="cdn-inv-from"
                  type="date"
                  value={invoiceDateFrom}
                  onChange={(e) => setInvoiceDateFrom(e.target.value)}
                  className="border-primary-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdn-inv-to">Invoice Date To</Label>
                <Input
                  id="cdn-inv-to"
                  type="date"
                  value={invoiceDateTo}
                  onChange={(e) => setInvoiceDateTo(e.target.value)}
                  className="border-primary-200"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  onClick={() => {
                    if (filtered.length === 0) {
                      toast.message('No rows to export');
                      return;
                    }
                    exportToCSV();
                  }}
                  variant="outline"
                  className="w-full border-primary-200 text-primary-700 hover:bg-primary-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800">Note list</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {selectedBranch == null ? (
              <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-pink-50 to-pink-100/50 rounded-lg border border-pink-200">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium mb-2">Select a branch</p>
                <p className="text-sm">Choose a branch from the header to view credit/debit notes.</p>
              </div>
            ) : loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="font-medium">No credit/debit notes found</p>
                <p className="text-sm">Try adjusting filters or create a note with New Note.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead className="font-semibold text-gray-700">Credit/Debit No</TableHead>
                      <TableHead className="font-semibold text-gray-700">Type</TableHead>
                      <TableHead className="font-semibold text-gray-700">Status</TableHead>
                      <TableHead className="font-semibold text-gray-700">Invoice No</TableHead>
                      <TableHead className="font-semibold text-gray-700 flex items-center gap-1">
                        Invoice Date
                        <span className="text-gray-400">↓</span>
                      </TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Amount</TableHead>
                      <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((row) => {
                      const noteNumber =
                        (row as { noteNumber?: string }).noteNumber ?? row.creditDebitNoteNo ?? `#${row.id}`;
                      const referenceInvoiceNo =
                        (row as { referenceInvoiceNo?: string }).referenceInvoiceNo ?? row.invoiceNo ?? '—';
                      const noteDate = (row as { noteDate?: string }).noteDate ?? row.creditDate;
                      const invoiceDate = row.invoiceDate ?? noteDate;
                      const statusNorm = normalizeListStatus(row);
                      const isEInvoiced = statusNorm === CreditDebitNoteListStatus.E_INVOICED;
                      return (
                        <TableRow key={row.id} className="hover:bg-primary-50/50">
                          <TableCell className="font-medium text-primary-700">{noteNumber}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                row.noteType === 'CREDIT_NOTE' || String(row.noteType).toUpperCase() === 'CREDIT'
                                  ? 'default'
                                  : 'secondary'
                              }
                            >
                              {row.noteType === 'DEBIT_NOTE' || String(row.noteType).toUpperCase() === 'DEBIT'
                                ? 'Debit Note'
                                : 'Credit Note'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={statusBadgeClass(statusNorm)}>
                              {statusDisplayLabel(statusNorm)}
                            </Badge>
                          </TableCell>
                          <TableCell>{referenceInvoiceNo}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="h-4 w-4 shrink-0" />
                              {invoiceDate ? formatDate(invoiceDate) : '—'}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-bold text-gray-900">
                            {row.grandTotal != null ? `₹ ${Number(row.grandTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleView(row.id)}
                                className="h-8 w-8 p-0 hover:bg-primary-50"
                                title="View note"
                              >
                                <Eye className="h-4 w-4 text-primary-600" />
                              </Button>
                              {!isEInvoiced && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(row.id)}
                                  className="h-8 w-8 p-0 hover:bg-emerald-50"
                                  title="Edit note"
                                >
                                  <Edit className="h-4 w-4 text-emerald-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
